import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as packsApi from '../api/programPacks';

export function ProgramPacks() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canAdmin = hasPermission('admin');
  const [availablePacks, setAvailablePacks] = useState([]);
  const [installedPacks, setInstalledPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [installing, setInstalling] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [available, installed] = await Promise.all([
        packsApi.getAvailablePacks(selectedOrg.id),
        packsApi.getInstalledPacks(selectedOrg.id)
      ]);
      setAvailablePacks(available);
      setInstalledPacks(installed);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isInstalled = (packId) => {
    return installedPacks.some(i => i.packId === packId);
  };

  const handleInstall = async (packId) => {
    if (!selectedOrg || installing) return;
    setInstalling(packId);
    try {
      await packsApi.installPack(selectedOrg.id, packId);
      loadData();
    } catch (err) {
      alert('Failed to install: ' + err.message);
    } finally {
      setInstalling(null);
    }
  };

  const handleUninstall = async (packId) => {
    if (!selectedOrg || installing) return;
    if (!window.confirm('Are you sure you want to uninstall this pack?')) return;
    setInstalling(packId);
    try {
      await packsApi.uninstallPack(selectedOrg.id, packId);
      loadData();
    } catch (err) {
      alert('Failed to uninstall: ' + err.message);
    } finally {
      setInstalling(null);
    }
  };

  const handleViewDetails = async (packId) => {
    if (!selectedOrg) return;
    try {
      const pack = await packsApi.getPack(selectedOrg.id, packId);
      setSelectedPack(pack);
      setShowDrawer(true);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!canAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need admin permissions to manage program packs.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Program Packs"
        subtitle="Install and manage program configuration packs"
      />

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading program packs...</div>
      ) : (
        <div style={styles.grid} data-testid="packs-grid">
          {availablePacks.map(pack => {
            const installed = isInstalled(pack.id);
            return (
              <div key={pack.id} style={styles.card} data-testid={`pack-${pack.id}`}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.packName}>{pack.name}</h3>
                  <span style={styles.version}>v{pack.version}</span>
                </div>
                <p style={styles.description}>{pack.description}</p>
                <div style={styles.contentsSummary}>
                  <span style={styles.contentItem}>
                    {pack.contents.fieldDefinitions.length} fields
                  </span>
                  <span style={styles.contentItem}>
                    {pack.contents.templates.length} templates
                  </span>
                  <span style={styles.contentItem}>
                    {pack.contents.reportProfiles.length} reports
                  </span>
                </div>
                <div style={styles.cardActions}>
                  <button
                    onClick={() => handleViewDetails(pack.id)}
                    style={styles.detailsButton}
                    data-testid={`view-${pack.id}`}
                  >
                    View Details
                  </button>
                  {installed ? (
                    <button
                      onClick={() => handleUninstall(pack.id)}
                      disabled={installing === pack.id}
                      style={styles.uninstallButton}
                      data-testid={`uninstall-${pack.id}`}
                    >
                      {installing === pack.id ? 'Uninstalling...' : 'Uninstall'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleInstall(pack.id)}
                      disabled={installing === pack.id}
                      style={styles.installButton}
                      data-testid={`install-${pack.id}`}
                    >
                      {installing === pack.id ? 'Installing...' : 'Install'}
                    </button>
                  )}
                </div>
                {installed && (
                  <div style={styles.installedBadge}>Installed</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showDrawer && selectedPack && (
        <div style={styles.drawerOverlay} onClick={() => setShowDrawer(false)}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()} data-testid="pack-drawer">
            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerTitle}>{selectedPack.name}</h3>
              <button onClick={() => setShowDrawer(false)} style={styles.closeButton}>×</button>
            </div>
            
            <div style={styles.drawerContent}>
              <div style={styles.drawerSection}>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Version</span>
                  <span>{selectedPack.version}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Author</span>
                  <span>{selectedPack.author}</span>
                </div>
              </div>

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Field Definitions ({selectedPack.contents.fieldDefinitions.length})</h4>
                <div style={styles.itemList}>
                  {selectedPack.contents.fieldDefinitions.map(field => (
                    <div key={field.key} style={styles.listItem}>
                      <span style={styles.itemKey}>{field.key}</span>
                      <span style={styles.itemLabel}>{field.label}</span>
                      <span style={styles.itemType}>{field.type}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Templates ({selectedPack.contents.templates.length})</h4>
                <div style={styles.itemList}>
                  {selectedPack.contents.templates.map(template => (
                    <div key={template.key} style={styles.listItem}>
                      <span style={styles.itemKey}>{template.key}</span>
                      <span style={styles.itemLabel}>{template.name}</span>
                      <span style={styles.itemType}>{template.type}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Report Profiles ({selectedPack.contents.reportProfiles.length})</h4>
                <div style={styles.itemList}>
                  {selectedPack.contents.reportProfiles.map(profile => (
                    <div key={profile.key} style={styles.listItem}>
                      <span style={styles.itemKey}>{profile.key}</span>
                      <span style={styles.itemLabel}>{profile.name}</span>
                      <span style={styles.itemType}>{profile.reportType}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Validation Rules ({selectedPack.contents.validationRules.length})</h4>
                <div style={styles.itemList}>
                  {selectedPack.contents.validationRules.map(rule => (
                    <div key={rule.key} style={styles.listItem}>
                      <span style={styles.itemKey}>{rule.key}</span>
                      <span style={styles.itemLabel}>{rule.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', position: 'relative' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  packName: { margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#2d3748' },
  version: { fontSize: '0.75rem', color: '#718096', background: '#f7fafc', padding: '0.25rem 0.5rem', borderRadius: '4px' },
  description: { margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#718096' },
  contentsSummary: { display: 'flex', gap: '1rem', marginBottom: '1rem' },
  contentItem: { fontSize: '0.75rem', color: '#4a5568', background: '#edf2f7', padding: '0.25rem 0.5rem', borderRadius: '4px' },
  cardActions: { display: 'flex', gap: '0.5rem' },
  detailsButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  installButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #38a169', background: '#38a169', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  uninstallButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e53e3e', background: 'white', color: '#e53e3e', fontSize: '0.875rem', cursor: 'pointer' },
  installedBadge: { position: 'absolute', top: '1rem', right: '1rem', padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#c6f6d5', color: '#276749', fontSize: '0.75rem', fontWeight: '500' },
  drawerOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  drawer: { width: '500px', maxWidth: '100%', background: 'white', height: '100%', overflow: 'auto', boxShadow: '-4px 0 6px rgba(0,0,0,0.1)' },
  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  drawerTitle: { margin: 0, fontSize: '1.25rem', fontWeight: '600' },
  closeButton: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' },
  drawerContent: { padding: '1.5rem' },
  drawerSection: { marginBottom: '1.5rem' },
  drawerRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  drawerLabel: { color: '#718096', fontSize: '0.875rem' },
  sectionTitle: { margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  itemList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  listItem: { display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem', background: '#f7fafc', borderRadius: '4px', fontSize: '0.75rem' },
  itemKey: { fontFamily: 'monospace', color: '#4a5568' },
  itemLabel: { flex: 1, color: '#2d3748' },
  itemType: { color: '#718096', background: '#edf2f7', padding: '0.125rem 0.25rem', borderRadius: '2px' }
};
